import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils.js';

interface CtLogoProps extends Omit<ComponentProps<'svg'>, 'children'> {
  /** `mark` = square icon only · `lockup` = mark + wordmark */
  variant?: 'mark' | 'lockup';
  /** Render with the brand gradient fill (default) or use currentColor (mono) */
  tone?: 'gradient' | 'mono';
  /** When variant is `lockup`, controls the wordmark text. */
  label?: string;
}

/**
 * CtLogo — Customertimes brand mark.
 *
 * Used in:
 * - Sidebar header (variant=lockup, tone=gradient)
 * - Empty states / login (variant=lockup, large)
 * - Mobile header / nav (variant=mark, tone=mono on dark)
 * - Loading splash (variant=mark with pulse-soft animation)
 *
 * Accessibility: provides an `aria-label` so screen readers announce
 * "Customertimes" rather than nothing.
 */
export function CtLogo({
  variant = 'mark',
  tone = 'gradient',
  label = 'Customertimes',
  className,
  ...props
}: CtLogoProps): React.ReactNode {
  if (variant === 'lockup') {
    return (
      <span
        className={cn('inline-flex items-center gap-2', className)}
        role="img"
        aria-label={`${label} — CT Agent`}
      >
        <CtMark tone={tone} className="size-7" />
        <span className="flex flex-col leading-none">
          <span className="font-heading text-sm font-semibold tracking-tight">
            Research
          </span>
          <span className="text-mini font-medium uppercase tracking-[0.14em] text-muted-foreground">
            by {label}
          </span>
        </span>
      </span>
    );
  }
  return (
    <CtMark tone={tone} className={className} {...props} aria-label={label} />
  );
}

interface CtMarkProps extends Omit<ComponentProps<'svg'>, 'children'> {
  tone?: 'gradient' | 'mono';
}

function CtMark({
  tone = 'gradient',
  className,
  ...props
}: CtMarkProps): React.ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      fill={tone === 'gradient' ? 'url(#ct-mark-gradient)' : 'currentColor'}
      xmlns="http://www.w3.org/2000/svg"
      className={cn('shrink-0', className)}
      {...props}
    >
      {tone === 'gradient' && (
        <defs>
          <linearGradient
            id="ct-mark-gradient"
            x1="0"
            y1="0"
            x2="0"
            y2="24"
            gradientUnits="userSpaceOnUse"
          >
            {/* Customertimes signature gradient — light-blue 100 → bright-blue 100 */}
            <stop offset="0%" stopColor="#46caff" />
            <stop offset="100%" stopColor="#1850ff" />
          </linearGradient>
        </defs>
      )}
      <path d="M12.7287 13.8233C12.7287 14.5839 12.7287 15.2627 12.7287 15.9476C10.0747 15.9476 8.72331 15.9354 4.88075 15.9476C1.89706 15.6553 -0.128024 12.7662 0.00630351 9.58886C0.14063 6.41969 2.41401 4.07656 6.04085 4.00091C6.1243 3.99886 17.7639 4.00091 24 4.00091C23.6702 4.76355 23.3833 5.42601 23.0617 6.1723C21.8406 6.1723 20.6357 6.1723 19.3534 6.1723C19.3534 9.4621 19.3534 12.6803 19.3534 15.9476C18.6188 15.9476 17.9655 15.9476 17.2164 15.9476C17.2164 12.7601 17.2164 9.46823 17.2164 6.1723C16.8074 6.14981 16.4837 6.1723 16.1602 6.1723C12.8386 6.16821 9.51707 6.16617 6.19553 6.1723C3.88143 6.17639 2.28376 7.63624 2.20438 9.78106C2.11483 12.1855 3.42757 13.6842 5.90652 13.8212C7.76268 13.9255 12.7938 13.8907 12.7287 13.8233Z" />
      <path d="M19.4097 18.3594C19.4097 19.1977 19.4097 19.9603 19.4097 20.7863C14.4214 20.7863 9.49193 20.7863 4.46484 20.7863C4.46484 19.995 4.46484 19.2099 4.46484 18.3594C9.43088 18.3594 14.3603 18.3594 19.4097 18.3594Z" />
    </svg>
  );
}
